/**
 * Evidence review queue — the UI half of D2's partial-seal authorization gate.
 *
 * When a generation run is cap-truncated, the seal is refused and the job parks as
 * AWAITING_REVIEW. Sealing it anyway is an attestation act: an admin must look at what is missing
 * and decide. The backend has served that deficiency since GET /ce/generation-jobs/{id}/review
 * landed, but nothing consumed it, so the decision was only reachable with curl.
 *
 * The load-bearing detail in here is small and easy to break: the digest POSTed back to
 * authorize-partial-seal is the one carried by the /review response that is on screen. That is the
 * entire point of D2's binding — the approval commits to the exact deficiency shown. Re-fetching
 * it, caching it from the job list, or holding it across a re-selection would still satisfy the
 * endpoint while quietly defeating the guarantee.
 *
 * AWAITING_REVIEW is not a failure. Generation succeeded and every child row is committed; the
 * gate refusing to seal is the system working. It is rendered amber/blue throughout, never red.
 *
 * ── Not buildable here: the permanent record of the authorization ────────────────────────────
 * The redesign asks for the authorization (who approved, when) to remain visible on the packet
 * afterward, not only in the moment. That is NOT possible frontend-only today. The authorization is
 * written into the sealed bundle and rendered into the PDF ("Partial export authorized by: …"), but
 * no API exposes it: GET /ce/bundles/{id}/manifest carries no authorization fields, and there is no
 * /ce/bundles/{id} or /authorization route (both 404). Verified on dev 2026-08-14 against a sealed
 * PARTIAL_REVIEW bundle whose PDF does contain the record.
 *
 * So this unit ships the in-the-moment receipt below and the permanent view is a tracked backend
 * item. It is deliberately NOT faked by re-deriving the values client-side and storing them.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { errorDetail } from '../services/api';
import { EvidenceClassBadge } from './CEBundleExport';
import { useAuth } from '../components/auth/AuthContext';
import { deficiencySentence, categoryLabel, categorySearchHref } from '../services/ceDeficiency';

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString() : '—';
}

function shortId(id) {
  return id ? `${String(id).slice(0, 8)}…` : '—';
}

/** Per-category detected/included, worst-first — the thing being approved. */
function CompletenessTable({ completeness, siteId, periodStart, periodEnd }) {
  const categories = (completeness && completeness.categories) || {};
  const rows = Object.entries(categories).sort(([, a], [, b]) => {
    if (a.complete !== b.complete) return a.complete ? 1 : -1;
    return (b.detected_count - b.included_count) - (a.detected_count - a.included_count);
  });
  if (!rows.length) {
    return <p className="text-xs text-gray-500">No per-category attestation recorded.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-700">
            <th className="pb-2 pr-4 font-medium">Category</th>
            <th className="pb-2 pr-4 font-medium text-right">Detected</th>
            <th className="pb-2 pr-4 font-medium text-right">Included</th>
            <th className="pb-2 pr-4 font-medium text-right">Not included</th>
            <th className="pb-2 font-medium">Limit</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700/60">
          {rows.map(([name, c]) => {
            const missing = (c.detected_count || 0) - (c.included_count || 0);
            return (
              <tr key={name}>
                <td className="py-2.5 pr-4 text-gray-300 text-xs">
                  {/* Linked only where a search type means the same thing — see
                      categorySearchHref. The three unmappable categories stay plain text rather
                      than getting a link that would quietly return the wrong set. */}
                  {(() => {
                    const href = categorySearchHref(name, { siteId, periodStart, periodEnd });
                    const label = categoryLabel(name);
                    return href ? (
                      <Link
                        to={href}
                        data-testid={`category-link-${name}`}
                        className="text-blue-400 hover:text-blue-300 underline decoration-dotted"
                      >
                        {label}
                      </Link>
                    ) : (
                      <span data-testid={`category-plain-${name}`}>{label}</span>
                    );
                  })()}
                </td>
                <td className="py-2.5 pr-4 text-right font-mono tabular-nums text-gray-200">
                  {c.detected_count}
                </td>
                <td className="py-2.5 pr-4 text-right font-mono tabular-nums text-gray-200">
                  {c.included_count}
                </td>
                <td
                  className={`py-2.5 pr-4 text-right font-mono tabular-nums ${
                    missing > 0 ? 'text-amber-300' : 'text-gray-500'
                  }`}
                >
                  {missing > 0 ? missing : '—'}
                </td>
                <td className="py-2.5 text-xs text-gray-500 font-mono">
                  {c.cap_exceeded ? `${c.cap_name} = ${c.cap_value}` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function CEEvidenceReview() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(searchParams.get('site') || '');
  const [jobs, setJobs] = useState([]);
  // siteId -> number of parked jobs, or null meaning "could not be determined".
  // null is deliberately NOT 0: a failed lookup that renders as "no packets awaiting" is the
  // silent-affirmation failure this whole branch exists to close, in miniature.
  const [parkedCounts, setParkedCounts] = useState({});
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [review, setReview] = useState(null);
  const [loadingReview, setLoadingReview] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  // The affirmation is state distinct from the action; see the attestation block below.
  const [affirmed, setAffirmed] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [authorizing, setAuthorizing] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [notice, setNotice] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // One list call per site. The API has no count endpoint and requires site_id, so the counts
  // come from the same query the queue itself uses. Promise.allSettled per CESiteOverview's
  // fan-out convention, with a rejected lookup recorded as null rather than collapsed to 0.
  const loadParkedCounts = useCallback(async (siteList) => {
    if (!siteList.length) return;
    const settled = await Promise.allSettled(
      siteList.map((s) =>
        api.get(`/ce/generation-jobs?site_id=${s.id}&state=AWAITING_REVIEW&limit=200`)),
    );
    if (!mountedRef.current) return;
    const next = {};
    siteList.forEach((s, i) => {
      const r = settled[i];
      next[s.id] = r.status === 'fulfilled' && Array.isArray(r.value.data)
        ? r.value.data.length
        : null;
    });
    setParkedCounts(next);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/ce/sites');
        if (!mountedRef.current) return;
        const list = Array.isArray(data) ? data : (data && data.sites) || [];
        setSites(list);
        setSiteId((current) => current || (list[0] && list[0].id) || '');
        if (!list.length) setLoadError('No CE sites registered.');
        loadParkedCounts(list);
      } catch (err) {
        if (mountedRef.current) setLoadError(errorDetail(err, 'Failed to load CE sites.'));
      }
    })();
  }, [loadParkedCounts]);

  // No polling: this queue only changes when a human acts on it. CEEvidencePacket's backoff
  // machinery exists because generation is long-running, which this is not.
  const loadJobs = useCallback(async (id) => {
    if (!id) return;
    setLoadingJobs(true);
    setLoadError(null);
    try {
      const { data } = await api.get(
        `/ce/generation-jobs?site_id=${id}&state=AWAITING_REVIEW&limit=200`,
      );
      if (!mountedRef.current) return;
      setJobs(Array.isArray(data) ? data : []);
    } catch (err) {
      if (mountedRef.current) {
        // The whole /ce router is admin-gated server-side while the routes themselves are not
        // role-gated client-side, so a non-admin who types the URL lands here. Say so plainly
        // rather than showing a bare failure.
        setLoadError(
          err && err.response && err.response.status === 403
            ? 'Reviewing evidence packets requires an administrator account.'
            : errorDetail(err, 'Failed to load jobs awaiting review.'),
        );
        setJobs([]);
      }
    } finally {
      if (mountedRef.current) setLoadingJobs(false);
    }
  }, []);

  useEffect(() => { loadJobs(siteId); }, [siteId, loadJobs]);

  async function selectJob(jobId) {
    setSelectedId(jobId);
    setReview(null);
    setReviewError(null);
    setAuthError(null);
    // Selecting a different packet clears the affirmation. An "I have reviewed this" that survived
    // a change of packet would let someone approve a deficiency they never looked at — the exact
    // thing the digest binding prevents server-side, so the UI must not undermine it client-side.
    setAffirmed(false);
    setReceipt(null);
    setLoadingReview(true);
    try {
      const { data } = await api.get(`/ce/generation-jobs/${jobId}/review`);
      if (!mountedRef.current) return;
      setReview(data);
    } catch (err) {
      if (mountedRef.current) {
        setReviewError(errorDetail(err, 'Failed to load the deficiency for this job.'));
      }
    } finally {
      if (mountedRef.current) setLoadingReview(false);
    }
  }

  async function authorize() {
    if (!review) return;
    setAuthorizing(true);
    setAuthError(null);
    try {
      // The digest comes from the review payload on screen — never from the job list, and never
      // re-fetched. See the module docstring.
      await api.post(`/ce/generation-jobs/${selectedId}/authorize-partial-seal`, {
        acknowledged_completeness_digest: review.pending_completeness_digest,
      });
      if (!mountedRef.current) return;
      // The receipt is captured from what was actually on screen and acted on — the digest the
      // authorization committed to, and the account that made the request. The authoritative
      // record of who and when lives inside the sealed bundle; nothing here invents a
      // server-side timestamp it cannot see.
      setReceipt({
        digest: review.pending_completeness_digest,
        siteName: review.site_name,
        username: (user && user.username) || null,
        at: new Date(),
      });
      setNotice(null);
      setAffirmed(false);
      setReview(null);
      setSelectedId(null);
      await loadJobs(siteId);
      await loadParkedCounts(sites);
    } catch (err) {
      // 409s here are written backend-side to be read by the operator — a stale digest, a job no
      // longer awaiting review, or a detected divergence that says outright not to proceed.
      // Surface them verbatim.
      if (mountedRef.current) setAuthError(errorDetail(err, 'Authorization failed.'));
    } finally {
      if (mountedRef.current) setAuthorizing(false);
    }
  }

  const selectedSite = sites.find((s) => s.id === siteId);

  return (
    <div className="bg-gray-900 text-gray-100 p-6 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Evidence Review</h1>
          <p className="text-sm text-gray-400 mt-1">
            Evidence packets that could not be sealed automatically because they are incomplete.
            Sealing one is an attestation — review what is missing before authorizing.
          </p>
        </div>

        {notice && (
          <div className="bg-blue-900/30 border border-blue-700 rounded-lg px-4 py-3 text-sm text-blue-300">
            {notice}
          </div>
        )}

        {/* What just happened, stated back. An attestation that vanishes the moment it is made
            leaves the person who made it unsure whether it registered — and this is the one action
            in the product where that uncertainty is least acceptable.

            Everything shown here is something this page actually observed: the digest that was on
            screen and was committed to, and the account that made the request. The authoritative
            record of who and when is written inside the sealed bundle by the backend; the time
            below is this browser's, and is labelled as such rather than presented as the sealed
            value. The sealed record is NOT yet readable through any API — see the note in the
            module header — so this receipt cannot currently be re-displayed later. */}
        {receipt && (
          <div
            data-testid="authorization-receipt"
            className="bg-green-900/25 border border-green-700 rounded-lg px-4 py-4 text-sm text-green-200 space-y-2"
          >
            <div className="font-semibold text-green-100">Authorized — this packet will be sealed</div>
            <p className="text-xs leading-relaxed">
              {receipt.username ? <><span className="font-mono">{receipt.username}</span> approved</> : 'You approved'}
              {' '}sealing the {receipt.siteName} packet as a bounded review set, at{' '}
              {receipt.at.toLocaleString()} (your local time). The worker will re-claim the job and
              complete the seal.
            </p>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-green-400/80">
                Deficiency approved (completeness digest)
              </div>
              <p className="font-mono text-[11px] break-all text-green-300/90">{receipt.digest}</p>
            </div>
            <p className="text-xs text-green-300/70">
              Your name and the authorization time are recorded inside the sealed record itself.
            </p>
          </div>
        )}

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Site</label>
            <select
              value={siteId}
              onChange={(e) => { setSiteId(e.target.value); setSelectedId(null); setReview(null); }}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select site</option>
              {sites.map((s) => {
                const n = parkedCounts[s.id];
                // Only three renderings, and the third is the point: unknown says so.
                const suffix = n === null ? ' — count unavailable'
                  : n > 0 ? ` — ${n} awaiting` : '';
                return (
                  <option key={s.id} value={s.id}>
                    {s.site_name} ({s.site_code}){suffix}
                  </option>
                );
              })}
            </select>
          </div>

          {loadError && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
              {loadError}
            </div>
          )}

          {loadingJobs && <p className="text-xs text-gray-500">Loading jobs awaiting review…</p>}

          {!loadingJobs && !loadError && jobs.length === 0 && (
            <div className="text-center py-12 text-gray-500 text-sm">
              No evidence packets are awaiting review
              {selectedSite ? ` for ${selectedSite.site_name}` : ''}. Packets appear here only when
              a generation run is truncated by a collection limit.
            </div>
          )}

          {jobs.length > 0 && (
            <div className="divide-y divide-gray-700/60">
              {jobs.map((j) => {
                const active = j.job_id === selectedId;
                return (
                  <button
                    key={j.job_id}
                    type="button"
                    onClick={() => selectJob(j.job_id)}
                    className={`w-full text-left py-2.5 px-2 -mx-2 rounded transition-colors ${
                      active ? 'bg-gray-700/60' : 'hover:bg-gray-750'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-gray-300 truncate">{j.job_id}</span>
                      <span className="shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full border bg-amber-900/50 text-amber-300 border-amber-700">
                        Awaiting review
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Submitted {fmt(j.submitted_at)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {selectedId && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 space-y-4" data-testid="review-detail">
            {loadingReview && <p className="text-xs text-gray-500">Loading the deficiency…</p>}

            {reviewError && (
              <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
                {reviewError}
              </div>
            )}

            {review && (
              <>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-sm font-semibold text-white">What you are authorizing</h2>
                    <p className="text-xs text-gray-500 mt-1">
                      {review.site_name} · {fmt(review.period_start)} → {fmt(review.period_end)}
                    </p>
                  </div>
                  <EvidenceClassBadge evidenceClass={review.evidence_class} />
                </div>

                {/* The deficiency in a sentence, BEFORE the table. The table answers "exactly how
                    much"; a reader needs "what am I being asked to approve" first, and a grid of
                    seven counts is not that. */}
                <p data-testid="deficiency-sentence" className="text-sm text-amber-200 leading-relaxed">
                  {deficiencySentence(review.completeness)}
                </p>

                <CompletenessTable
                  completeness={review.completeness}
                  siteId={review.site_id}
                  periodStart={review.period_start}
                  periodEnd={review.period_end}
                />

                {Array.isArray(review.incomplete_categories) && review.incomplete_categories.length > 0 && (
                  <div className="bg-amber-900/30 border border-amber-700 rounded-lg px-3 py-2 text-xs text-amber-300">
                    Incomplete: <span className="font-mono">{review.incomplete_categories.join(', ')}</span>.
                    Sealing this packet records it as a bounded review set, not a complete evidence packet.
                  </div>
                )}

                <div>
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Completeness digest
                  </div>
                  <p className="font-mono text-xs text-gray-400 break-all">
                    {review.pending_completeness_digest}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Your authorization is bound to this exact deficiency. If the underlying data
                    changes before the seal, the digest no longer matches and the authorization is
                    refused rather than applied to something you did not review.
                  </p>
                </div>

                {authError && (
                  <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 text-sm text-red-300">
                    {authError}
                  </div>
                )}

                {/* ── The attestation ──────────────────────────────────────────────────────────
                    Deliberately heavier than anything else in the product, because it is the one
                    place a human takes personal responsibility for sealing a knowingly incomplete
                    record.

                    The affirmation is a SEPARATE ACT from the action: the checkbox states what is
                    being attested to in the first person, and the button cannot fire until it is
                    ticked. The previous two-step confirm could be completed by clicking the same
                    place twice, which is a speed bump, not an attestation. */}
                <div className="bg-gray-900/60 border border-amber-800/60 rounded-lg p-4 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="attestation-checkbox"
                      checked={affirmed}
                      onChange={(e) => setAffirmed(e.target.checked)}
                      disabled={authorizing}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-600 bg-gray-700 accent-purple-600"
                    />
                    <span className="text-sm text-amber-100 leading-relaxed">
                      I have reviewed the deficiency above and approve sealing this packet as a
                      bounded review set. My name and the time will be recorded inside the sealed
                      record, alongside the digest above.
                    </span>
                  </label>

                  <button
                    type="button"
                    data-testid="authorize-button"
                    onClick={authorize}
                    disabled={!affirmed || authorizing}
                    className="px-5 py-2 text-sm rounded-lg bg-purple-700 hover:bg-purple-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium transition-colors"
                  >
                    {authorizing ? 'Authorizing…' : 'Authorize partial seal'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
